import { useEffect, useState } from 'react';

export type TextAreaProps = {
    /**
     * Html id for the input.
     */
    id?: string;

    /**
     * The title explaining the use of the input.
     */
    title?: string;

    /**
     * Associate this input with a form.
     */
    form?: string;

    /**
     * The value of the name attribute on the input element.
     */
    name?: string;

    /**
     * The value of the placeholder attribute on the input element.
     */
    isOptional?: boolean;

    /**
     * The visible height (lines) of the textarea
     */
    rows?: number;
    /**
     * An initial value for the input.
     */
    defaultValue?: string;

    /**
     *The value of the placeholder attribute on the input element.
     */
    placeholder?: string;

    /**
     * Displays a hint under the component.
     */
    hint?: string;

    /**
     * Max number characters allowed.
     */
    characterLimit?: number;

    /**
     * Display an error message under the input in red.
     */
    errorMessage?: string;

    /**
     * Automatically set the focus of the page to this element
     */
    autoFocus?: boolean;

    /**
     * Disable the input of text.
     */
    isDisabled?: boolean;

    /**
     * Callback function invoked when the value is changed
     */
    onChange?: (value: string) => void;
};

function TextArea({
    title,
    onChange,
    defaultValue,
    name,
    form,
    id,
    hint,
    characterLimit,
    placeholder,
    errorMessage,
    isOptional = false,
    rows = 4,
    autoFocus = false,
    isDisabled = false,
}: TextAreaProps): JSX.Element {
    const [value, setValue] = useState(defaultValue || '');

    useEffect(() => {
        setValue(defaultValue || '');
    }, [defaultValue]);
    const colorStyles = errorMessage
        ? 'text-red-800 dark:text-red-400 placeholder-red-800 dark:placeholder-red-400 focus:outline-none focus:ring-0 border-red-600 dark:border-red-400'
        : 'text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-600 focus:border-indigo-600 dark:focus:border-indigo-400 border-gray-200 dark:border-white/[0.07]';

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        const { value } = e.target;

        const val = characterLimit !== undefined ? value.substring(0, characterLimit) : value;

        setValue(val);
        if (onChange) {
            onChange(val);
        }
    }

    return (
        <div>
            <div className="flex justify-between">
                {/* Show a title */}
                {title && <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>}
                {/* Show an optional message */}
                {isOptional && <span className="text-sm text-gray-500 ml-auto mb-1">Optional</span>}
            </div>

            <div>
                <textarea
                    id={id}
                    name={name}
                    form={form}
                    autoFocus={autoFocus}
                    rows={rows}
                    className={`dark:bg-white/5 whitespace-pre-line shadow-sm block w-full sm:text-sm rounded-md ${colorStyles} ${
                        isDisabled ? 'opacity-50' : ''
                    }`}
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={isDisabled}
                />
            </div>
            <div className="flex">
                {/* Hint */}
                {hint && !errorMessage && <p className="mt-2 text-sm text-gray-500">{hint}</p>}
                {/* Error Message */}
                {errorMessage && <p className="mt-2 text-sm text-red-800 dark:text-red-400">{errorMessage}</p>}

                {characterLimit !== undefined && !errorMessage && (
                    <p className="mt-2 text-sm text-gray-500 ml-auto">
                        {value?.length || 0} / {characterLimit}
                    </p>
                )}
            </div>
        </div>
    );
}
export default TextArea;
